import {
  createApiRef,
  DiscoveryApi,
  IdentityApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface SimpleManifestMetadataLink {
  url: string;
  title?: string;
  icon?: string;
  type?: string;
}

export interface SimpleManifestKeyValue {
  key: string;
  value: string;
}

export interface SimpleManifestAnnotationsSection {
  allowedKeys: string[];
  editable: SimpleManifestKeyValue[];
  locked: SimpleManifestKeyValue[];
}

export interface SimpleManifestEditableFields {
  manifestSha: string;
  entity: {
    kind: string;
    namespace: string;
    name: string;
    title?: string;
  };
  metadata: {
    name: string;
    namespace?: string;
    description?: string;
    tags: string[];
    labels: SimpleManifestKeyValue[];
    links: SimpleManifestMetadataLink[];
    annotations: SimpleManifestAnnotationsSection;
  };
  spec: {
    type?: string;
    lifecycle?: string;
    owner?: string;
    subcomponentOf?: string;
    providesApis: string[];
    consumesApis: string[];
    dependsOn: string[];
    dependencyOf: string[];
  };
  options?: {
    linkIconOptions: string[];
    componentTypeOptions?: string[];
    typeOptions?: string[];
  };
}

export interface SimpleManifestUpdateRequest {
  entityRef: string;
  manifestSha: string;
  metadata: {
    name: string;
    namespace?: string;
    description?: string;
    tags: string[];
    labels: SimpleManifestKeyValue[];
    links: SimpleManifestMetadataLink[];
    annotations: SimpleManifestKeyValue[];
  };
  spec: {
    type?: string;
    lifecycle?: string;
    owner?: string;
    subcomponentOf?: string;
    providesApis?: string[];
    consumesApis?: string[];
    dependsOn?: string[];
    dependencyOf?: string[];
  };
  title?: string;
  description?: string;
}

export interface ManifestPullRequestResponse {
  branchName: string;
  pullRequestNumber: number;
  pullRequestUrl: string;
  commitSha: string;
  autoMerged?: boolean;
  autoMergeError?: string;
}

export interface SimpleManifestEditorApi {
  getEditableManifest(
    entityRef: string,
  ): Promise<SimpleManifestEditableFields>;
  createPullRequest(
    update: SimpleManifestUpdateRequest,
  ): Promise<ManifestPullRequestResponse>;
}

export const simpleManifestEditorApiRef =
  createApiRef<SimpleManifestEditorApi>({
    id: 'plugin.simple-manifest-editor.service',
  });

export class SimpleManifestEditorClient implements SimpleManifestEditorApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
    this.fetchApi = options.fetchApi;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl(
      'simple-manifest-editor',
    );
    const { token } = await this.identityApi.getCredentials();

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Simple manifest editor request failed (${response.status}): ${message}`,
      );
    }

    return (await response.json()) as T;
  }

  async getEditableManifest(
    entityRef: string,
  ): Promise<SimpleManifestEditableFields> {
    const search = new URLSearchParams({ entityRef });
    return this.request<SimpleManifestEditableFields>(
      `/entity?${search.toString()}`,
    );
  }

  async createPullRequest(
    update: SimpleManifestUpdateRequest,
  ): Promise<ManifestPullRequestResponse> {
    return this.request<ManifestPullRequestResponse>('/entity/pull-request', {
      method: 'POST',
      body: JSON.stringify(update),
    });
  }
}
